import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import type { Exercise, ProgramExercise, SessionSet } from "../../types/database";
import { EXERCISE_LIBRARY, type LibraryExercise } from "../../data/exerciseLibrary";
import { FULL_PROGRAM } from "../../data/fullProgram";

export interface ExerciseWithTarget extends ProgramExercise {
  exercise: Exercise;
}

export interface LastPerformance {
  weight_kg: number | null;
  reps: number | null;
  rir: number | null;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

export function useSessionLogging() {
  const [programId, setProgramId] = useState<string | null>(null);
  const [programName, setProgramName] = useState<string | null>(null);
  const [allExercises, setAllExercises] = useState<ExerciseWithTarget[]>([]);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [lastByExercise, setLastByExercise] = useState<Record<string, LastPerformance>>({});
  const [loggedByExercise, setLoggedByExercise] = useState<Record<string, SessionSet[]>>({});
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) {
      setLoading(false);
      return;
    }

    const { data: program } = await supabase
      .from("programs")
      .select("id, name")
      .eq("is_active", true)
      .maybeSingle();

    if (!program) {
      setProgramId(null);
      setProgramName(null);
      setAllExercises([]);
      setLoading(false);
      return;
    }
    setProgramId(program.id);
    setProgramName(program.name);

    const { data: programExercises } = await supabase
      .from("program_exercises")
      .select("*, exercise:exercises(*)")
      .eq("program_id", program.id)
      .order("order_index", { ascending: true });
    const list = (programExercises ?? []) as unknown as ExerciseWithTarget[];
    setAllExercises(list);
    setSelectedDay((prev) => prev ?? list[0]?.day_label ?? null);

    // Última serie registrada por ejercicio, para precargar peso/reps/rir con un solo tap.
    const lastEntries = await Promise.all(
      list.map(async (pe) => {
        const { data } = await supabase
          .from("session_sets")
          .select("weight_kg, reps, rir")
          .eq("exercise_id", pe.exercise_id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        return [pe.exercise_id, data ?? { weight_kg: null, reps: null, rir: null }] as const;
      })
    );
    setLastByExercise(Object.fromEntries(lastEntries));

    const { data: existingSession } = await supabase
      .from("workout_sessions")
      .select("id")
      .eq("date", todayIso())
      .maybeSingle();

    if (existingSession) {
      setSessionId(existingSession.id);
      const { data: sets } = await supabase
        .from("session_sets")
        .select("*")
        .eq("session_id", existingSession.id);
      const grouped: Record<string, SessionSet[]> = {};
      for (const s of sets ?? []) {
        (grouped[s.exercise_id] ??= []).push(s);
      }
      setLoggedByExercise(grouped);
    } else {
      setSessionId(null);
      setLoggedByExercise({});
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Días distintos del programa, en el orden en que aparecen sus ejercicios.
  const days = useMemo(() => {
    const seen: string[] = [];
    for (const e of allExercises) {
      const d = e.day_label ?? "General";
      if (!seen.includes(d)) seen.push(d);
    }
    return seen;
  }, [allExercises]);

  const exercises = useMemo(
    () => allExercises.filter((e) => (e.day_label ?? "General") === (selectedDay ?? days[0])),
    [allExercises, selectedDay, days]
  );

  async function ensureSession(): Promise<string> {
    if (sessionId) return sessionId;
    const { data: userData } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from("workout_sessions")
      .insert({ user_id: userData.user!.id, date: todayIso() })
      .select("id")
      .single();
    if (error) throw error;
    setSessionId(data.id);
    return data.id;
  }

  async function logSet(
    exerciseId: string,
    values: { weight_kg: number | null; reps: number | null; rir: number | null }
  ) {
    const sid = await ensureSession();
    const existingCount = loggedByExercise[exerciseId]?.length ?? 0;
    const { data, error } = await supabase
      .from("session_sets")
      .insert({
        session_id: sid,
        exercise_id: exerciseId,
        set_number: existingCount + 1,
        ...values,
      })
      .select("*")
      .single();
    if (error) throw error;
    setLoggedByExercise((prev) => ({
      ...prev,
      [exerciseId]: [...(prev[exerciseId] ?? []), data],
    }));
    setLastByExercise((prev) => ({ ...prev, [exerciseId]: values }));
  }

  async function insertLibraryExercise(userId: string, lib: LibraryExercise) {
    const { data, error } = await supabase
      .from("exercises")
      .insert({
        user_id: userId,
        name: lib.name,
        muscle_group: lib.muscleGroup,
        equipment: lib.equipment,
        instructions: lib.instructions,
        image_urls: lib.imageUrls,
      })
      .select("id")
      .single();
    if (error) throw error;
    return data.id as string;
  }

  // Genera un programa completo de 4 días para recomposición corporal (pecho 2x/semana,
  // tal como marcaba el boceto), usando la librería curada en español. Pensado para quien
  // no tiene un programa propio y quiere empezar a entrenar ya con algo bien planteado.
  async function generateFullProgram() {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user!.id;

    await supabase.from("programs").update({ is_active: false }).eq("user_id", userId).eq("is_active", true);

    const { data: program, error: programError } = await supabase
      .from("programs")
      .insert({ user_id: userId, name: "Recomposición · Fase 1", is_active: true })
      .select("id")
      .single();
    if (programError) throw programError;

    let orderIndex = 0;
    for (const day of FULL_PROGRAM) {
      for (const item of day.exercises) {
        const lib = EXERCISE_LIBRARY.find((e) => e.id === item.id);
        if (!lib) continue;
        const exerciseId = await insertLibraryExercise(userId, lib);
        const { error: linkError } = await supabase.from("program_exercises").insert({
          program_id: program.id,
          exercise_id: exerciseId,
          order_index: orderIndex++,
          day_label: day.label,
          target_sets: item.sets,
          target_reps_min: item.repsMin,
          target_reps_max: item.repsMax,
          target_rir: item.rir,
          notes: item.notes ?? null,
        });
        if (linkError) throw linkError;
      }
    }

    setSelectedDay(null);
    await load();
  }

  // Añade un ejercicio de la librería al programa activo (o crea uno nuevo si no había).
  async function addExerciseToProgram(
    entry: LibraryExercise,
    targets: { sets: number; repsMin: number; repsMax: number; rir: number | null },
    dayLabel: string | null
  ) {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user!.id;

    let targetProgramId = programId;
    if (!targetProgramId) {
      const { data: newProgram, error: newProgramError } = await supabase
        .from("programs")
        .insert({ user_id: userId, name: "Mi programa", is_active: true })
        .select("id")
        .single();
      if (newProgramError) throw newProgramError;
      targetProgramId = newProgram.id;
    }

    const exerciseId = await insertLibraryExercise(userId, entry);

    const { error: linkError } = await supabase.from("program_exercises").insert({
      program_id: targetProgramId,
      exercise_id: exerciseId,
      order_index: allExercises.length,
      day_label: dayLabel,
      target_sets: targets.sets,
      target_reps_min: targets.repsMin,
      target_reps_max: targets.repsMax,
      target_rir: targets.rir,
    });
    if (linkError) throw linkError;

    await load();
  }

  // Quita un ejercicio del programa (no borra el historial de series ya registradas,
  // solo el enlace program_exercises, para poder ir cambiando el programa con el tiempo).
  async function removeExercise(programExerciseId: string) {
    const { error } = await supabase.from("program_exercises").delete().eq("id", programExerciseId);
    if (error) throw error;
    await load();
  }

  return {
    programName,
    days,
    selectedDay: selectedDay ?? days[0] ?? null,
    setSelectedDay,
    exercises,
    lastByExercise,
    loggedByExercise,
    loading,
    logSet,
    generateFullProgram,
    addExerciseToProgram,
    removeExercise,
  };
}
